import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * Área de texto del sistema de diseño.
 *
 * Mismo aspecto que `Input` (borde `line-strong`, radio de 10px, foco con halo azul) pero
 * con altura mínima para textos largos: descripción del negocio, políticas, personalidad
 * del asistente, condiciones de un método de pago.
 *
 * Igual que `Input`, se usa dentro de un `Field` (que aporta la etiqueta y el error).
 */

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Marca el campo en rojo (error de validación). */
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid = false, rows = 4, ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={cn(
        'scroll-thin w-full rounded-control border bg-surface px-3 py-2 text-body text-ink transition-colors',
        'placeholder:text-ink-faint',
        'focus:outline-none focus-visible:outline-none focus-visible:ring-4',
        'disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-muted',
        invalid
          ? 'border-danger focus-visible:border-danger focus-visible:ring-danger-soft'
          : 'border-line-strong focus-visible:border-brand-500 focus-visible:ring-brand-100',
        className,
      )}
      {...props}
    />
  );
});
